import { yupResolver } from "@hookform/resolvers/yup";
import React, { useCallback, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as yup from "yup";

import {
	CERTIFICATION_TYPES,
	OTHER_DOCUMENT_TYPES,
} from "../../../configs/constants/DocumentTypes";
import { POLICE_EXTRACT_TYPES } from "../../../configs/constants/PoliceExtractTypes";
import { Documents } from "../../../models/Consultant";
import { KeyValue } from "../../../models/KeyValue";
import { enumToKeyValueArray } from "../../../utils/object.utils";
import AppText from "../../Core/AppText/AppText";
import Button from "../../Core/Button/Button";
import { DropdownController } from "../../Core/Dropdown/Dropdown";
import Modal from "../../Core/Modal/Modal";
import Flex from "../../Layout/Flex";
import FileImporter from "../FileImporter/FileImporter";
import { useMediaStore } from "../../../stores/media.store";

interface ModalParamProps {
	onCancelModal: Function;
	updateDocuments: Function;
	documents?: Documents;
	/**
	 * Candidates (recruitment) can still upload certificate-type documents —
	 * the recruitment checklist builds on them. For consultants certificates
	 * live on the "Certifikat & taggar" tab, so only other document types are
	 * offered here.
	 */
	isCandidate?: boolean;
}

type DocumentTypeRequest = {
	documentType: CERTIFICATION_TYPES | OTHER_DOCUMENT_TYPES | "";
	policeExtractTypeKey?: string;
};

const documentTypeSchema = yup.object().shape({
	documentType: yup.string().required("FIELD_IS_REQUIRED"),
	policeExtractTypeKey: yup.string().when("documentType", {
		is: CERTIFICATION_TYPES.POLICE_RECORD_EXTRACTS,
		then: (schema) => schema.required("FIELD_IS_REQUIRED"),
		otherwise: (schema) => schema.optional(),
	}),
});

const DocumentUploadModal: React.FC<ModalParamProps> = ({
	onCancelModal,
	documents,
	updateDocuments,
	isCandidate,
}) => {
	const { t } = useTranslation();

	const mediaStore = useMediaStore();
	const { document, documentLoading } = mediaStore;

	// Certificates (incl. police-record extracts) are no longer uploaded through
	// the document library — they are assigned with proof on the Kompetens tab
	// (ConsultantCredentialsPanel, profile.certifications) for both consultants
	// and candidates. The library only takes CV/other documents.
	const options = enumToKeyValueArray({ ...OTHER_DOCUMENT_TYPES });
	const policeExtractOptions: KeyValue[] = POLICE_EXTRACT_TYPES.map(({ key, labelKey }) => ({
		key,
		value: labelKey,
	}));

	const [option] = useState<KeyValue>();
	const [dropZoneError, setDropZoneError] = useState<string>("");

	const { handleSubmit, formState, control } = useForm<DocumentTypeRequest>({
		mode: "onBlur",
		resolver: yupResolver(documentTypeSchema),
		defaultValues: { documentType: "", policeExtractTypeKey: "" },
	});

	const watchedDocumentType = useWatch({ control, name: "documentType" });
	const showPoliceExtractTypeDropdown =
		watchedDocumentType === CERTIFICATION_TYPES.POLICE_RECORD_EXTRACTS;

	const onSubmit = handleSubmit(async (e) => {
		if (document) {
			if (Object.values(CERTIFICATION_TYPES)?.includes(e.documentType as CERTIFICATION_TYPES)) {
				const isPoliceExtract = e.documentType === CERTIFICATION_TYPES.POLICE_RECORD_EXTRACTS;

				documents = {
					...documents,
					certifications: [
						...(documents?.certifications || []),
						{
							type: e.documentType as CERTIFICATION_TYPES,
							url: document.url,
							name: document.originalName,
							mimeType: document.mimeType,
							visibleToConsultant: false,
							...(isPoliceExtract && e.policeExtractTypeKey
								? { policeExtractTypeKey: e.policeExtractTypeKey }
								: {}),
						},
					],
				};
			} else if (
				Object.values(OTHER_DOCUMENT_TYPES)?.includes(e.documentType as OTHER_DOCUMENT_TYPES)
			) {
				documents = {
					...documents,
					otherDocuments: [
						...(documents?.otherDocuments || []),
						{
							type: e.documentType as OTHER_DOCUMENT_TYPES,
							url: document.url,
							name: document.originalName,
							mimeType: document.mimeType,
							visibleToConsultant: false,
						},
					],
				};
			}

			await updateDocuments(documents);
			mediaStore.removeDocument();
			onCancelModal();
		} else {
			setDropZoneError(t("FIELD_IS_REQUIRED"));
		}
	});

	const hideModal = useCallback(
		(e: any) => {
			e.preventDefault();
			mediaStore.removeDocument();
			onCancelModal?.();
		},
		[onCancelModal, mediaStore]
	);

	return (
		<form onSubmit={onSubmit}>
			<Modal maxWidth="570px" padding="2.4375rem 4rem 3.125rem 4rem">
				<AppText size="xm" weight="bold" margin="0 0 23px 0">
					{t("UPLOAD_DOCUMENT")}
				</AppText>
				<Flex direction="column">
					<Flex margin="0 0 35px 0">
						<FileImporter dropZoneError={dropZoneError} />
					</Flex>

					<Flex direction="column">
						<AppText size="xms" weight="bold">
							{t("DOCUMENT_TYPE")}
						</AppText>
						<DropdownController<KeyValue>
							control={control}
							name="documentType"
							options={options}
							placeholder="CHOOSE_A_DOCUMENT_TYPE"
							value={option}
							getValue={(option) => option.key as string}
							getOptionLabel={(option: KeyValue) => t(option.value as any)}
							errors={formState.errors?.documentType}
							padding="0 0 0 0"
							height="150px"
						/>
					</Flex>

					{showPoliceExtractTypeDropdown && (
						<Flex direction="column">
							<AppText size="xms" weight="bold">
								{t("POLICE_EXTRACT_TYPE")}
							</AppText>
							<DropdownController<KeyValue>
								control={control}
								name="policeExtractTypeKey"
								options={policeExtractOptions}
								placeholder="SELECT_POLICE_EXTRACT_TYPE"
								getValue={(option) => option.key as string}
								getOptionLabel={(option: KeyValue) => t(option.value as any)}
								errors={formState.errors?.policeExtractTypeKey}
								padding="0 0 0 0"
								height="150px"
							/>
						</Flex>
					)}
				</Flex>
				<ModalButtonWrapper>
					<Button
						disabled={formState.isSubmitting || documentLoading}
						width="220px"
						margin="2.5rem 0 0 0"
						fontSize="s"
						height="34px"
					>
						{t("UPLOAD")}
					</Button>
					<Button
						onClick={(e: any) => hideModal(e)}
						width="220px"
						margin="0.625rem 0 0 0"
						textColor="black"
						backgroundColor="buttonGray"
						fontSize="s"
						height="34px"
					>
						{t("SEND_MESSAGE_CANCEL")}
					</Button>
				</ModalButtonWrapper>
			</Modal>
		</form>
	);
};
export default DocumentUploadModal;

const ModalButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	flex-direction: column;
`;
